import React, { useCallback, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useLocalStorage } from "./useLocalStorage";
import { User } from "../models/responses";
import { AuthContext, AuthContextType } from "./authContext";
import { actions as apartmentFilterActions } from "@/store/slices/apartmentFiltersSlice";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useLocalStorage<User | null>("user", null);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const login = useCallback(
        (data: User) => {
            setUser(data);
            navigate("/");
        },
        [setUser, navigate],
    );

    const logout = useCallback(() => {
        // Clear apartment filters before user state: `resetOnLogout` also removes
        // the `apartmentFilters.*` localStorage keys so the next user on the same
        // browser profile starts unfiltered — no cross-user leak (FR-012, EC-006,
        // apartment-filters-everywhere).
        dispatch(apartmentFilterActions.resetOnLogout());
        setUser(null);
        navigate("/login", { replace: true });
    }, [dispatch, navigate, setUser]);

    const value = useMemo(
        () => ({
            user,
            login,
            logout,
        }),
        [login, logout, user],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
    return useContext<AuthContextType>(AuthContext);
};
